
import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Hash, TrendingUp } from 'lucide-react';

interface FakePlayCountDialogProps {
  game: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const FakePlayCountDialog: React.FC<FakePlayCountDialogProps> = ({ 
  game, 
  open, 
  onOpenChange 
}) => {
  const [fakePlayCount, setFakePlayCount] = useState(0);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (game) {
      setFakePlayCount(game.fake_play_count || game.play_count || 0);
    }
  }, [game]);

  // Update fake play count mutation
  const updateFakePlayCountMutation = useMutation({
    mutationFn: async (count: number) => {
      const { error } = await supabase
        .from('games')
        .update({ fake_play_count: count })
        .eq('id', game.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-games'] });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      onOpenChange(false);
      toast.success('Play count updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update play count: ' + error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (fakePlayCount < 0) {
      toast.error('Play count cannot be negative');
      return;
    }
    updateFakePlayCountMutation.mutate(fakePlayCount);
  };

  const handleQuickSet = (amount: number) => {
    setFakePlayCount(amount);
  };

  if (!game) return null;

  const realPlayCount = game.play_count || 0;
  const currentFakeCount = game.fake_play_count || game.play_count || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Hash className="w-5 h-5 text-purple-600" />
            Update Play Count: {game?.title}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Current Stats */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="text-center">
              <div className="text-sm text-gray-600">Real Plays</div>
              <div className="text-lg font-semibold text-green-600">
                {realPlayCount.toLocaleString()}
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600">Display Plays</div>
              <div className="text-lg font-semibold text-blue-600">
                {currentFakeCount.toLocaleString()}
              </div>
            </div>
          </div>

          {/* Input */}
          <div className="space-y-2">
            <Label htmlFor="fakePlayCount">
              Display Play Count
            </Label>
            <Input
              id="fakePlayCount"
              type="number"
              value={fakePlayCount}
              onChange={(e) => setFakePlayCount(parseInt(e.target.value) || 0)}
              min="0"
              className="text-lg"
            />
            <p className="text-xs text-gray-600">
              This is the number users will see. Real play count will remain unchanged.
            </p>
          </div>

          {/* Quick Set Buttons */}
          <div className="space-y-3">
            <Label>Quick Set Options:</Label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleQuickSet(1000)}
                className="text-xs"
              >
                1K Plays
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleQuickSet(5000)}
                className="text-xs"
              >
                5K Plays
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleQuickSet(10000)}
                className="text-xs"
              >
                10K Plays
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleQuickSet(25000)}
                className="text-xs"
              >
                25K Plays
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleQuickSet(50000)}
                className="text-xs"
              >
                50K Plays
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleQuickSet(100000)}
                className="text-xs"
              >
                100K Plays
              </Button>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleQuickSet(realPlayCount)}
              className="w-full text-xs"
            >
              Reset to Real Count ({realPlayCount.toLocaleString()})
            </Button>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={updateFakePlayCountMutation.isPending}
              className="min-w-[100px]"
            >
              {updateFakePlayCountMutation.isPending ? 'Saving...' : 'Update'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FakePlayCountDialog;
